import { useEffect, useState } from "react";
import Api from "../../api/api";
import stringHelpers from "../../../utilities/stringHelpers";
import { dateRange } from "../../../utilities/dateUtilities";
import Enums from "../../../utilities/enum";
import { ScheduleExportDateRangePicker, isScheduleDateRangeCustomRangeInvalid } from "../scheduleExportDateRangePicker";
import { SearchSelect } from "../../shared/searchSelect";
import { ScheduleExportModal } from "../../shared/scheduleExportModal";
import { TagSelector } from "../../shared/tagSelector";
import arrayUtilities from "../../../utilities/arrayUtilities";

export const allowListReportType = 1

export const AllowListScheduleModal = ({ sites, clients = [], tags = [], organisationId, token, onCancel, onSaved, scheduledExport, initialParameters, initialSitesByClient }) => {
    const seedParameters = scheduledExport?.parameters ?? initialParameters ?? {}
    const isEditing = !stringHelpers.isNullOrEmpty(scheduledExport?.id)
    const hasCustomDates = seedParameters.from != null || seedParameters.to != null
    const [scheduleFromDate, setScheduleFromDate] = useState(seedParameters.from ?? dateRange("lastMonth").firstDate)
    const [scheduleToDate, setScheduleToDate] = useState(seedParameters.to ?? dateRange("lastMonth").lastDate)
    const [scheduledDateRangeTypePreset, setScheduledDateRangeTypePreset] = useState(seedParameters.reportDatePreset || (hasCustomDates ? null : 0))
    const [scheduleClientId, setScheduleClientId] = useState(seedParameters.clientId ?? null)
    const [scheduleSitesByClient, setScheduleSitesByClient] = useState(initialSitesByClient ?? sites)
    const [scheduleSiteId, setScheduleSiteId] = useState(seedParameters.siteId ?? null)
    const [scheduleCreatedByUserId, setScheduleCreatedByUserId] = useState(seedParameters.userId ?? null)
    const [scheduleCreatedBySource, setScheduleCreatedBySource] = useState(seedParameters.createdBySource ?? null)
    const [createdByOptions, setCreatedByOptions] = useState([])
    const [scheduleFilterPeriodType, setScheduleFilterPeriodType] = useState(String(seedParameters.allowedVrmFilterDateType ?? 0))
    const [scheduleSelectedTags, setScheduleSelectedTags] = useState(seedParameters.tagIds ?? [])
    const [isSaving, setIsSaving] = useState(false)
    const [saveErrorMessage, setSaveErrorMessage] = useState(null)

    const isScheduleCustomDateRangeInvalid = isScheduleDateRangeCustomRangeInvalid(scheduledDateRangeTypePreset, scheduleFromDate, scheduleToDate)
    const isSubmitDisabled = isScheduleCustomDateRangeInvalid || isSaving

    const getCreatedByOptionValue = item => !stringHelpers.isNullOrEmpty(item.userId) ? item.userId : item.name
    const selectedCreatedByValue = !stringHelpers.isNullOrEmpty(scheduleCreatedByUserId) ? scheduleCreatedByUserId : scheduleCreatedBySource

    useEffect(() => {
        Api.getAllowListCreatedBy(
            data => setCreatedByOptions(data),
            error => console.log(error),
            organisationId,
            token
        )

        const needsSavedClientSites = !initialSitesByClient && !stringHelpers.isNullOrEmpty(seedParameters.clientId)
        if (needsSavedClientSites) {
            Api.getClientSites(
                data => setScheduleSitesByClient(data),
                error => console.log(error),
                seedParameters.clientId,
                organisationId,
                token
            )
        }
    }, [])

    const handleScheduleClientSelect = (value) => {
        if (stringHelpers.isNullOrEmpty(value)) {
            setScheduleClientId(null)
            setScheduleSitesByClient(sites)
            setScheduleSiteId(null)
        } else {
            setScheduleClientId(value)
            Api.getClientSites(
                data => {
                    setScheduleSitesByClient(data)
                    setScheduleSiteId(null)
                },
                error => console.log(error),
                value,
                organisationId,
                token
            )
        }
    }

    const handleCreatedBySelect = (value) => {
        if (stringHelpers.isNullOrEmpty(value)) {
            setScheduleCreatedByUserId(null)
            setScheduleCreatedBySource(null)
            return
        }

        const selectedOption = createdByOptions.find(item => getCreatedByOptionValue(item) === value)
        if (stringHelpers.isNullOrEmpty(selectedOption.userId)) {
            setScheduleCreatedByUserId(null)
            setScheduleCreatedBySource(selectedOption.name)
        } else {
            setScheduleCreatedByUserId(selectedOption.userId)
            setScheduleCreatedBySource(null)
        }
    }

    const buildScheduledExportParameters = () => {
        const isUsingDateRangePreset = scheduledDateRangeTypePreset !== null
        const dateRangeParameters = isUsingDateRangePreset
            ? { reportDatePreset: scheduledDateRangeTypePreset }
            : { from: scheduleFromDate, to: scheduleToDate }

        return {
            ...dateRangeParameters,
            siteId: !stringHelpers.isNullOrEmpty(scheduleSiteId) ? scheduleSiteId : null,
            clientId: !stringHelpers.isNullOrEmpty(scheduleClientId) ? scheduleClientId : null,
            userId: !stringHelpers.isNullOrEmpty(scheduleCreatedByUserId) ? scheduleCreatedByUserId : null,
            createdBySource: !stringHelpers.isNullOrEmpty(scheduleCreatedBySource) ? scheduleCreatedBySource : null,
            allowedVrmFilterDateType: Number(scheduleFilterPeriodType),
            tagIds: !arrayUtilities.isNullOrEmpty(scheduleSelectedTags) ? scheduleSelectedTags : null,
        }
    }

    const handleSaveFailed = (error) => {
        setSaveErrorMessage(error?.errorMessage ?? "Unable to save scheduled export")
        setIsSaving(false)
    }

    const handleSaveSucceeded = (data) => {
        if (data?.isError) {
            handleSaveFailed(data)
        } else {
            onSaved()
        }
    }

    return (
        <ScheduleExportModal
            initialValues={ scheduledExport }
            reportType={ allowListReportType }
            onCancel={ onCancel }
            disableConfirm={ isSubmitDisabled }
            errorMessage={ saveErrorMessage }
            onSubmit={ (isActive, name, scheduleFrequencyMode, monday, tuesday, wednesday, thursday, friday, saturday, sunday, dayOfMonth, time, recipients) => {
                setIsSaving(true)
                setSaveErrorMessage(null)
                if (isEditing) {
                    Api.updateScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        scheduledExport.id,
                        name, allowListReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                } else {
                    Api.createScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        name, allowListReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                }
            } }
        >
            <div className="">
                <h5 className="mb-2">Filters</h5>
                <div className="row mb-2">
                    <div className="col-md-3">Date Range</div>
                    <div className="col-md-9">
                        <ScheduleExportDateRangePicker
                            scheduledDateRangeTypePreset={ scheduledDateRangeTypePreset }
                            setScheduledDateRangeTypePreset={ setScheduledDateRangeTypePreset }
                            scheduleFromDate={ scheduleFromDate }
                            setScheduleFromDate={ setScheduleFromDate }
                            scheduleToDate={ scheduleToDate }
                            setScheduleToDate={ setScheduleToDate }
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Client</div>
                    <div className="col-md-9">
                        <SearchSelect
                            width="100%"
                            options={ [{ value: "", label: "All" }, ...clients.map(item => ({ value: item.id, label: item.name }))] }
                            onSelect={ handleScheduleClientSelect }
                            selected={ scheduleClientId }
                            defaultLabel="All"
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Site</div>
                    <div className="col-md-9">
                        <SearchSelect
                            width="100%"
                            options={ [{ value: "", label: "All" }, ...scheduleSitesByClient.map(item => ({ value: item.id, label: item.name }))] }
                            onSelect={ value => setScheduleSiteId(value) }
                            selected={ scheduleSiteId }
                            defaultLabel="All"
                        />
                    </div>
                </div>
                { !arrayUtilities.isNullOrEmpty(tags) && <div className="row mb-2">
                    <div className="col-md-3">Tags</div>
                    <div className="col-md-9">
                        <TagSelector
                            onChange={ tagSelections => setScheduleSelectedTags(tagSelections) }
                            selectedTags={ scheduleSelectedTags }
                            tags={ tags }
                        />
                    </div>
                </div> }
                <div className="row mb-2">
                    <div className="col-md-3">Created By</div>
                    <div className="col-md-9">
                        <SearchSelect
                            width="100%"
                            options={ [{ value: "", label: "All" }, ...createdByOptions.map(item => ({ value: getCreatedByOptionValue(item), label: item.name }))] }
                            onSelect={ value => handleCreatedBySelect(value) }
                            selected={ selectedCreatedByValue }
                            defaultLabel="All"
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Filter by Period Type</div>
                    <div className="col-md-9">
                        <SearchSelect
                            defaultLabel="All"
                            width="100%"
                            options={ Object.entries(Enums.FilterPeriodType).map(([key, value]) => ({ value: key, label: value })) }
                            onSelect={ value => setScheduleFilterPeriodType(value) }
                            selected={ scheduleFilterPeriodType }
                        />
                    </div>
                </div>
            </div>
            <hr className="border-dashed border-bottom-0"></hr>
        </ScheduleExportModal>
    )
}
